import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import Badge from '../../Badge/badge';

export interface TabItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  title: string;
  id: string;
  badgeText?: string;
  handleClick: () => void;
  isActive: boolean;
  isNested?: boolean;
  className?: string;
}

const tabItemVariantStyle = cva(
  [
    'preflight',
    'ds-transition',
    'ds-duration-100',
    'ds-text-nowrap',
    '!ds-font-sans',
    'ds-text-base',
    'ds-p-2',
    'active:ds-text-neutrals-white',
    'focus-visible:ds-ring-neutrals-black',
    'focus-visible:ds-ring-2',
    'focus-visible:ds-outline-none',
    'focus-visible:ds-ring-offset-2'
  ],
  {
    variants: {
      isNested: {
        true: [
          'ds-rounded-xl',
          'ds-my-2',
          'ds-mx-1',
          'ds-border',
          'ds-border-neutrals-lighter',
          'hover:ds-bg-neutrals-light',
          'active:ds-bg-neutrals-dark',
          'focus-visible:ds-ring-neutrals-black',
          'focus-visible:ds-ring-2',
          'focus-visible:ds-outline-none',
          'focus-visible:ds-ring-offset-2',
          'focus-visible:ds-bg-neutrals-white'
        ],
        false: [
          'ds-rounded',
          'ds-rounded-b-none',
          'ds-border-b-2',
          'ds-border-b-neutrals-white',
          'ds-my-1',
          'ds-mr-2',
          'hover:ds-bg-acuity-green-lighter',
          'hover:ds-border-b-acuity-green-lighter',
          'active:ds-bg-acuity-green-darkest',
          'active:ds-border-b-acuity-green-darkest',
          'focus-visible:ds-bg-acuity-green-lightest',
          'focus-visible:ds-border-b-acuity-green-lightest'
        ]
      }
    }
  }
);

export const TabItem = ({
  title,
  id,
  handleClick,
  isActive,
  isNested = false,
  badgeText = '',
  className = '',
  ...props
}: TabItemProps) => {
  const getTabItemClasses = (isNested: boolean, isActive: boolean, className: string) => {
    return twMerge(
      tabItemVariantStyle({ isNested }),
      isActive && !isNested && '!ds-border-b-acuity-green',
      isActive && isNested && 'ds-bg-neutrals-white !ds-border-neutrals',
      className
    );
  };

  return (
    <button
      className={getTabItemClasses(isNested, isActive, className)}
      role="tab"
      id={`tab-${id}`}
      aria-controls={`panel-${id}`}
      aria-selected={isActive}
      onClick={handleClick}
      {...props}
    >
      {title}
      {badgeText && (
        <Badge
          label={badgeText}
          className="ds-ml-2 ds-h-5 ds-bg-neutrals-white ds-text-neutrals-black"
        />
      )}
    </button>
  );
};
